import * as Sentry from "@sentry/nextjs"
import { useElements, useStripe } from "@stripe/react-stripe-js"
import { ExpressCheckoutElement } from "@stripe/react-stripe-js"
import { Stripe, StripeElements, StripeExpressCheckoutElementConfirmEvent } from "@stripe/stripe-js"
import { Button, Checkbox, Spinner, TextInput } from "flowbite-react"
import { useFormikContext } from "formik"
import Image from "next/image"
import React, { useEffect, useState } from "react"

import { LeftSmallIcon } from "@assets/icons"
import FormGroup from "@components/elements/FormGroup"
import StripeCardCvcInput from "@components/elements/StripeCardCvcInput"
import StripeCardExpiryInput from "@components/elements/StripeCardExpiryInput"
import StripeCardNumberInput from "@components/elements/StripeCardNumberInput"
import useConfirm from "@hooks/useConfirm"
import useToast from "@hooks/useToast"
import CautionIcon from "@icons/Caution"
import { CartItemDetailsType } from "@modules/tenants/OrdersPage/Components/AddOrderModal"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import { PaymentTypes, ValidateGiftcardCodeResponseType } from "@mono/essentials/types/CartTypes"
import {
  CustomerTypeOrder,
  GetSavedCardsOfCustomerResponseType,
  PayOrderRequestType,
} from "@mono/essentials/types/OrderTypes"

import PaymentAmountCard from "../AmountCard"
import { PaymentAmountDetails } from "../PaymentModal"

import { StripeCreditCardFormAttributes } from "."

interface StripeCreditCardFormDataProps {
  handleClose: () => void
  isModal?: boolean
  hideDefaultContainer?: boolean
  error?: string
  cartDetails?: CartItemDetailsType
  cartId?: string
  refetchCartData?: () => void
  paymentAmountDetails?: PaymentAmountDetails
  goToPrevStep?: () => void
  hideBackButton?: boolean
  payOrderTipDetails?: PayOrderRequestType["tip"]
  onlyTipPayment?: boolean
  isIntegratedPage?: boolean
  savedCardsInfo?: GetSavedCardsOfCustomerResponseType
  customer?: CustomerTypeOrder
  onSavedCardDelete?: () => void
  isWidgetsCard?: boolean
  giftcardDetails?: ValidateGiftcardCodeResponseType["giftcard"]
  isPublicPayment?: boolean
  stripeRef: React.MutableRefObject<Stripe | null>
  elementsRef: React.MutableRefObject<StripeElements | null>
  isWalletsEnabled?: boolean
  handleWalletPayment: (
    values: StripeCreditCardFormAttributes,
    setSubmitting?: (isSubmitting: boolean) => void
  ) => Promise<void>
}

const CARD_BRAND_LOGO = ["amex", "visa", "mastercard"]

const StripeCreditCardFormData = ({
  handleClose,
  hideDefaultContainer,
  error,
  cartDetails,
  cartId,
  refetchCartData,
  goToPrevStep,
  paymentAmountDetails,
  hideBackButton = false,
  payOrderTipDetails,
  onlyTipPayment = false,
  isIntegratedPage,
  savedCardsInfo,
  customer,
  onSavedCardDelete,
  giftcardDetails,
  isWidgetsCard,
  isPublicPayment = false,
  stripeRef,
  elementsRef,
  isWalletsEnabled,
  handleWalletPayment,
}: StripeCreditCardFormDataProps) => {
  const stripe = useStripe()
  const elements = useElements()
  const confirm = useConfirm()
  const { showToast } = useToast()
  const {
    setFieldValue,
    isSubmitting,
    isValid,
    getFieldProps,
    errors,
    touched,
    resetForm,
    values,
    handleSubmit,
    setSubmitting,
  } = useFormikContext<StripeCreditCardFormAttributes>()
  const [cardNumberError, setCardNumberError] = useState(true)
  const [cardExpiryError, setCardExpiryError] = useState(true)
  const [cardCvcError, setCardCvcError] = useState(true)

  useEffect(() => {
    stripeRef.current = stripe
  }, [stripe, stripeRef])

  useEffect(() => {
    elementsRef.current = elements
  }, [elements, elementsRef])

  useEffect(() => {
    if (paymentAmountDetails?.isDepositEnabledForItems) {
      setFieldValue("amount", paymentAmountDetails?.depositAmount)
      setFieldValue("paymentType", PaymentTypes.DEPOSIT_AMOUNT)
    } else if (!paymentAmountDetails?.isDepositEnabledForItems) {
      setFieldValue("paymentType", PaymentTypes.FULL_AMOUNT)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentAmountDetails?.depositAmount, paymentAmountDetails?.isDepositEnabledForItems])

  useEffect(() => {
    if ((cardCvcError || cardExpiryError || cardNumberError) && !values.savedCardId) {
      setFieldValue("hasPaymentValidData", false)
    } else {
      setFieldValue("hasPaymentValidData", true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardCvcError, cardExpiryError, cardNumberError])

  const removeSavedCard = async (cardId: string) => {
    try {
      const choice = await confirm({
        title: `Are you sure you want to delete the saved card?`,
        confirmBtnLabel: "Yes, I'm sure",
        icon: <CautionIcon className={"mx-auto mb-4"} />,
      })

      if (choice) {
        if (customer?.id) await OrderServices.deleteSavedCardOfCustomer(customer?.id, cardId)
        onSavedCardDelete?.()
        showToast({ heading: "Card Deleted successfully!" })
      }
    } catch (e: any) {
      Sentry.captureException(e)
      showToast({
        heading: e?.response?.data?.message ?? "Unable to remove the card! please try again later.",
        type: "danger",
      })
    }
  }

  const isNonZeroPayment =
    values.paymentType !== PaymentTypes.DEPOSIT_AMOUNT ||
    (!isNaN(parseFloat(values.amount)) && parseFloat(values.amount) > 0) ||
    isIntegratedPage

  return (
    <>
      {isSubmitting ? (
        <div
          className={
            "absolute top-0 left-0 w-full h-full flex items-center justify-center bg-black opacity-30 z-50"
          }
        >
          <Spinner aria-label={"Center-aligned spinner example"} className={"text-center my-6"} />
        </div>
      ) : null}

      {isNonZeroPayment && savedCardsInfo?.length ? (
        <>
          <div className={`flex flex-col items-start gap-2 w-full md:px-6 px-0`}>
            <p className={"text14Medium text-neutral-1000"}>Add Payment</p>
            <div className={"w-full h-px bg-neutral-200 mb-1"} />
          </div>
          <div className={`flex flex-col items-center gap-3 md:mx-6 mx-0 sm:mb-3 mt-1 sm:mt-5`}>
            {savedCardsInfo.map((savedCard) => (
              <div
                key={savedCard.id}
                className={`flex flex-row items-center justify-between p-4 ${
                  values.savedCardId === savedCard.id
                    ? "bg-primary-200 border-primary-500"
                    : "bg-whiteBlack-100 border-neutral-200"
                } border rounded-md gap-4 w-full`}
              >
                <div className={"flex flex-row items-center justify-start gap-3"}>
                  <Image
                    priority
                    src={`/images/${CARD_BRAND_LOGO.includes(savedCard.brand) ? savedCard.brand : "creditCard"}.svg`}
                    alt={""}
                    width={54}
                    height={36}
                  />
                  <div className={"flex flex-col items-start gap-1 flex-1 w-full"}>
                    {savedCard.name_on_card && (
                      <div className={"text14Semibold text-neutral-900"}>
                        {savedCard.name_on_card}
                      </div>
                    )}
                    <div className={"flex flex-row items-center gap-1"}>
                      <p className={"text14Medium text-neutral-900 capitalize"}>
                        {savedCard.brand}
                      </p>
                      <p className={"text12Regular text-neutral-500"}>**** {savedCard.last4}</p>
                      {savedCard.exp_month && savedCard.exp_year ? (
                        <>
                          <div className={"w-0.5 bg-neutral-400 rounded-full h-3.5 mx-0.5"} />
                          <p className={"text12Medium text-neutral-500"}>
                            Exp. {savedCard.exp_month}/{savedCard.exp_year}
                          </p>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className={"flex flex-row items-center gap-4"}>
                  <button
                    type={"button"}
                    className={"cursor-pointer text12Medium text-primary-600"}
                    onClick={() => removeSavedCard(savedCard.id)}
                  >
                    Remove
                  </button>
                  <Checkbox
                    checked={values.savedCardId === savedCard.id}
                    onChange={() => {
                      setFieldValue("isUsingSavedCard", true)
                      setFieldValue("savedCardId", savedCard.id)
                    }}
                    className={`h-4 w-4 rounded-xl`}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
      {/* Add new card block */}
      {isNonZeroPayment ? (
        <div className={"w-full"}>
          {isWalletsEnabled && (
            <div className={`mb-4 sm:mx-6 mx-2`}>
              <ExpressCheckoutElement
                onConfirm={() => handleWalletPayment(values, setSubmitting)}
                options={{
                  wallets: {
                    applePay: "always",
                    googlePay: "always",
                  },
                  buttonTheme: {
                    applePay: "white-outline",
                    googlePay: "white",
                  },
                }}
              />
            </div>
          )}
          <div
            className={`flex flex-col items-start p-4 border rounded-md md:mx-6 mx-0 ${
              !values.savedCardId
                ? "bg-primary-200 border-primary-500"
                : "bg-whiteBlack-100 border-neutral-200"
            }`}
          >
            {!isWidgetsCard ? (
              <div className={"flex flex-row items-center justify-between gap-2 w-full"}>
                <div className={"flex flex-row items-center justify-start gap-3"}>
                  <Image priority src={"/images/creditCard.svg"} alt={""} width={54} height={36} />
                  <p className={"text14Semibold text-neutral-900"}>Add New Card</p>
                </div>
                <Checkbox
                  checked={!values.savedCardId}
                  onChange={() => {
                    setFieldValue("isUsingSavedCard", false)
                    setFieldValue("savedCardId", "")
                  }}
                  className={`h-4 w-4 rounded-xl`}
                />
              </div>
            ) : null}
            {!values.savedCardId ? (
              <div className={"flex flex-col mb-2 w-full"}>
                {!isWidgetsCard ? (
                  <div className={"w-full h-px bg-primary-400 mt-3.5 mb-5"} />
                ) : null}
                <FormGroup label={"Name on Card"} name={"name"} requiredField>
                  <TextInput
                    {...getFieldProps("name")}
                    placeholder={"Enter Name"}
                    color={errors.name && touched.name ? "failure" : undefined}
                    data-testid={"card-name-element"}
                    className={`bg-whiteBlack-100 ${isWidgetsCard ? "[&>div>input]:border-neutral-450" : ""}`}
                  />
                </FormGroup>
                <StripeCardNumberInput
                  onChange={(_, error) => {
                    setCardNumberError(Boolean(error))
                    setFieldValue("hasPaymentValidData", !error)
                  }}
                  className={"mt-4"}
                  data-testid={"card-number-element"}
                  isWidgetsCard={isWidgetsCard}
                />
                <div className={"mt-4 flex flex-row items-center gap-4"}>
                  <StripeCardExpiryInput
                    onChange={(_, error) => {
                      setCardExpiryError(Boolean(error))
                      setFieldValue("hasPaymentValidData", !error)
                    }}
                    className={"flex-1"}
                    isWidgetsCard={isWidgetsCard}
                  />
                  <StripeCardCvcInput
                    onChange={(_, error) => {
                      setCardCvcError(Boolean(error))
                      setFieldValue("hasPaymentValidData", !error)
                    }}
                    className={"flex-1"}
                    isWidgetsCard={isWidgetsCard}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {!hideDefaultContainer && (
        <div
          className={`flex flex-col items-start gap-2 w-full px-6 ${isNonZeroPayment ? "mt-8" : ""}`}
        >
          <p className={"text14Medium text-neutral-1000"}>Payment options</p>
          <div className={"w-full h-px bg-neutral-200"} />
        </div>
      )}
      {!hideDefaultContainer && (
        <PaymentAmountCard
          className={"mt-5"}
          cartId={cartId}
          cartDetails={cartDetails}
          onSuccess={async () => refetchCartData?.()}
          paymentAmountDetails={paymentAmountDetails}
          payOrderTipDetails={payOrderTipDetails}
          onlyTipPayment={onlyTipPayment}
          enableCustomAmount={!giftcardDetails}
          giftcardDetails={giftcardDetails}
          enableSaveCard={!isPublicPayment}
        />
      )}
      {error ? (
        <p
          className={`${isWidgetsCard ? `!m-0 !mt-2 md:px-6 px-0` : "mx-0 mb-3 px-6"}  text14Regular text-red-600`}
        >
          {error}
        </p>
      ) : null}
      {!hideDefaultContainer && (
        <div
          className={`
            flex flex-1 flex-row items-center border-t border-neutral-200 bg-whiteBlack-100 px-6 pt-4
            justify-between
          `}
        >
          {!hideBackButton ? (
            <Button color={"ghost"} size={"md"} onClick={goToPrevStep}>
              <LeftSmallIcon width={18} height={18} className={"mr-2"} />
              Back
            </Button>
          ) : (
            <div />
          )}
          <div className={"flex flex-row items-center gap-3"}>
            <Button
              outline
              size={"md"}
              onClick={() => {
                handleClose()
                resetForm()
              }}
            >
              Cancel
            </Button>
            <Button
              size={"md"}
              color={"primary"}
              onClick={handleSubmit}
              disabled={
                !isValid ||
                isSubmitting ||
                (values.paymentType === PaymentTypes.SAVE_CARD ? values.isUsingSavedCard : false) ||
                (values.savedCardId
                  ? !values.savedCardId
                  : isNonZeroPayment
                    ? cardNumberError || cardExpiryError || cardCvcError
                    : false)
              }
            >
              {isSubmitting ? (
                <Spinner size={"sm"} light />
              ) : values.paymentType === PaymentTypes.SAVE_CARD ? (
                "Save"
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </div>
      )}
    </>
  )
}

export default StripeCreditCardFormData
